import { BehaviorSubject, combineLatest } from "rxjs";
import { ApiConnectService } from "../../Services/ApiConnectService";
import { TournamentDataService } from "../../Services/TournamentDataService";
import { navbarData } from "../../components/navbar/Navbar.ViewModel";
import { eventViewModel } from "../../EventsDashboard/EventsDashboard/EventsDashboard.ViewModel";
import { tdfTransfer } from '../../Models/Inbound/TdfTransfer';
import { standingsReportTransfer } from "../../Models/Inbound/StandingsReportTransfer";
import { json2xml, xml2json } from "xml-js";
import { RequestResult } from "../../Models/Common/RequestResult";
import { tdfUploadConfirmation } from "../../Models/Outbound/TdfUploadConfirmation";
import { liveStandings } from "../../Models/Inbound/LiveStandings";
import { eventState } from "../../Models/Common/EventState";
import { tdfFinalTransfer } from "../../Models/Inbound/TdfFinalTransfer";
import { loadingState } from "../../Models/Common/LoadingState";
import { competitorDivision } from "../../Models/Common/CompetitorDivision";

export class unsavedChangesViewModel {
  isAnyChanges: boolean;
  isTournamentFileChanged: boolean;
  isStandingsChanged: boolean;
}

export enum apiSampleTabOptions {
  liveResults,
  pairings,
  standings,
  endOfRound,
}

export class selectedTournamentFileViewModel {
  file: string = "";
  fileName: string = "";
  gameType: string = "";
  isUploading: boolean = false;
  uploadResult: RequestResult<tdfUploadConfirmation> | null;
}

export class selectedStandingsViewModel {
  value: string = "";
  division: competitorDivision = competitorDivision.Master;
  round: number = 0;
  calculationMessage: string = "";
  isUploading: boolean = false;
  uploadResult: RequestResult<liveStandings> | null;
}

export class apiSampleViewModel {
  selectedTab: apiSampleTabOptions = apiSampleTabOptions.endOfRound;
  isLoading: boolean = false;
  response: string = "";
}

export class scorekeeperDashboardViewModel {
    navbarData: BehaviorSubject<navbarData>;
    eventId: string;
    apiSampleViewModel = new BehaviorSubject<apiSampleViewModel>(new apiSampleViewModel());
    selectedTournamentFileViewModel = new BehaviorSubject<selectedTournamentFileViewModel>(new selectedTournamentFileViewModel());
    selectedStandingsViewModel = new BehaviorSubject<selectedStandingsViewModel>(new selectedStandingsViewModel());
    loadingStateSubject = new BehaviorSubject<loadingState>(loadingState.loading);
    eventViewModelSubject = new BehaviorSubject<eventViewModel | null>(null);
    unsavedChanges = new BehaviorSubject<unsavedChangesViewModel | null>(null);
    parser = new DOMParser();

    acs = new ApiConnectService();
    tournamentService = new TournamentDataService(this.acs);

    constructor(
      eventId: string,
      navbarDataSubject: BehaviorSubject<navbarData>,
      roundNumber: number | null,
      isReadOnly: boolean = false
    ) {
        this.eventId = eventId;
        console.log("Scorekeeper dashboard created for EventId: " + eventId);
        this.refreshData();
        this.navbarData = navbarDataSubject;
    }

    refreshData = () => {
      this.tournamentService.GetEventById(this.eventId).subscribe(apiData => {
      if (apiData.result) {
          var eventData = eventViewModel.fromApiEvent(apiData.result);
          this.eventViewModelSubject.next(eventData ?? null);
          console.log("Scorekeeper dashboard updated")
          this.updateResultsTab(this.apiSampleViewModel.value.selectedTab);
          setTimeout(() => {
            this.loadingStateSubject.next(loadingState.success);
          }, 500);
          setTimeout(() => {
              this.loadingStateSubject.next(loadingState.hidden);
          }, 1800);
        } else {
          this.loadingStateSubject.next(loadingState.error);
        }
      });
    }

    switchResultsTab = (tab: apiSampleTabOptions) => {
      var apiSampleModel = this.apiSampleViewModel.value;
      apiSampleModel.selectedTab = tab;
      this.apiSampleViewModel.next(apiSampleModel);
      this.updateResultsTab(tab);
    }
  
    uploadTournamentFile = (shouldCalculateStandings: boolean, shouldStoreTdf: boolean, isFinalUpload: boolean) => {
      var selectedFile = this.selectedTournamentFileViewModel.value;
      selectedFile.isUploading = true
      this.selectedTournamentFileViewModel.next(selectedFile);

      if (isFinalUpload) {
        var tdfFinalTransfer: tdfFinalTransfer = {
          eventId: this.eventId,
          tdfFile: selectedFile.file
        };
        console.log("Starting file upload for EventId: " + this.eventId);
        this.tournamentService.UploadFinalTdfFile(tdfFinalTransfer, this.eventId!).subscribe(result => {
          if (result) {
            var selectedFile = this.selectedTournamentFileViewModel.value;
            selectedFile.uploadResult = result
            selectedFile.file = "";
            selectedFile.isUploading = false
            this.selectedTournamentFileViewModel.next(selectedFile)
          }
        });
      } else {
        var tdfTransfer: tdfTransfer = {
          id: this.eventId,
          tdfFile: selectedFile.file,
          shouldCalculateStandings : shouldCalculateStandings,
          shouldStoreTdf : shouldStoreTdf
        };
        console.log("Starting file upload for EventId: " + this.eventId);
        this.tournamentService.UploadTdfFile(tdfTransfer, this.eventId!).subscribe(result => {
          if (result) {
            var selectedFile = this.selectedTournamentFileViewModel.value;
            selectedFile.uploadResult = result
            selectedFile.file = "";
            selectedFile.isUploading = false
            this.selectedTournamentFileViewModel.next(selectedFile)
          }
        });
      }
    }

    exportTdf = () => {
      console.log("Starting Export TDF for EventId: " + this.eventId);
      this.tournamentService.GetExportedTdf(this.eventId!).subscribe(result => {
        if (result.result) {
          try {
            // Create a Blob from the XML string
            const blob = new Blob([result.result.tdfFile], { type: 'application/xml' });

            // Create a link element
            const link = document.createElement('a');
            
            // Create a URL for the Blob and set it as the href
            link.href = URL.createObjectURL(blob);
            
            // Set the download attribute to specify the file name
            link.download = this.eventId + '.tdf';

            // Append the link to the body (necessary for Firefox)
            document.body.appendChild(link);

            // Programmatically click the link to trigger the download
            link.click();

            // Remove the link after downloading
            document.body.removeChild(link);
          } catch (error) {
            console.error('Error downloading XML:', error);
          }
        };
      });
    }

    uploadStandings = () => {
      var selectedStandings = this.selectedStandingsViewModel.value;
      selectedStandings.isUploading = true
      this.selectedStandingsViewModel.next(selectedStandings)
      var standingsUpload: standingsReportTransfer = {
        eventId: this.eventId,
        reportText: selectedStandings.value,
        division: selectedStandings.division,
        round: selectedStandings.round
      };
      console.log("Starting standings upload for EventId: " + this.eventId);
      this.tournamentService.PostTournamentStandingsReport(standingsUpload, this.eventId!).subscribe(result => {
        var selectedStandings = this.selectedStandingsViewModel.value;
        selectedStandings.uploadResult = result
        selectedStandings.value = "";
        selectedStandings.division = competitorDivision.Master;
        selectedStandings.round = 0;
        selectedStandings.calculationMessage = "";
        selectedStandings.isUploading = false
        this.selectedStandingsViewModel.next(selectedStandings)
      });
    }

    updateSelectedTournamentFile = (file: string) => {
      function nativeType(value: any) {
        var nValue = Number(value);
        if (!isNaN(nValue)) {
          return nValue;
        }
        var bValue = value.toLowerCase();
        if (bValue === 'true') {
          return true;
        } else if (bValue === 'false') {
          return false;
        }
        return value;
      }
      
      var removeJsonTextAttribute = function(value: any, parentElement: any) {
        try {
          var keyNo = Object.keys(parentElement._parent).length;
          var keyName = Object.keys(parentElement._parent)[keyNo - 1];
          parentElement._parent[keyName] = nativeType(value);
        } catch (e) {}
      }
      
      var options = {
        compact: true,
        trim: true,
        ignoreDeclaration: true,
        ignoreInstruction: true,
        ignoreAttributes: false,
        ignoreComment: true,
        ignoreCdata: true,
        ignoreDoctype: true,
        textFn: removeJsonTextAttribute
      };
      

      var selectedFileModel = this.selectedTournamentFileViewModel.value;
      selectedFileModel.file = file;
      var rawTournament = JSON.parse(xml2json(file, options));
      selectedFileModel.fileName = rawTournament["tournament"]["data"]["name"];
      switch (rawTournament["tournament"]["_attributes"]["gametype"])
      {
        case "TRADING_CARD_GAME":
          selectedFileModel.gameType = 'Trading Card Game'
            break;
        case "VIDEO_GAME":
          selectedFileModel.gameType = 'Video Game'
            break;
        default:
          selectedFileModel.gameType = ''
            break;
      }
      this.selectedTournamentFileViewModel.next(selectedFileModel);
    }

    standingsReportDivisionChange = (value: competitorDivision) => {
      var selectedStandings = this.selectedStandingsViewModel.value;
      selectedStandings.division = value;
      this.selectedStandingsViewModel.next(selectedStandings);
    }
  
    manuallySetStandingsReportRoundValue = (value: number) => {
      var selectedStandings = this.selectedStandingsViewModel.value;
      selectedStandings.round = value;
      this.selectedStandingsViewModel.next(selectedStandings);
    }

    updateSelectedStandingsValue = (standings: string) => {
      var selectedStandings = this.selectedStandingsViewModel.value;
      try {
        var calculatedRound = 0;
        var firstRecord = standings.substring(
          standings.indexOf("/") - 2, standings.indexOf("/") + 4);
  
          firstRecord.split("/").forEach(function (result) {
          calculatedRound += Number(result);
        });

        if (calculatedRound > 0) {
          selectedStandings.round = calculatedRound;
          selectedStandings.calculationMessage = "Round number detected - ready to upload!";
        }
        else {
          selectedStandings.round = 0;
          selectedStandings.calculationMessage = "⚠️ Error reading standings.";
        }
      }
      catch {
        selectedStandings.round = 0;
        selectedStandings.calculationMessage = "⚠️ Error reading standings.";
      }
      selectedStandings.value = standings;
      this.selectedStandingsViewModel.next(selectedStandings);
    };

    private getPairingsTicker = (eventId: string) => {
      // console.log("Get pairings ticker");
      var apiSampleModel = this.apiSampleViewModel.value;
      apiSampleModel.isLoading = true;
      this.apiSampleViewModel.next(apiSampleModel);
      this.tournamentService.GetPairingsTicker(eventId!).subscribe(apiResult => {
        var apiSampleModel = this.apiSampleViewModel.value;
        apiSampleModel.isLoading = false;
        apiSampleModel.response = apiResult?.result ?? apiResult.summaryTitle;
        this.apiSampleViewModel.next(apiSampleModel);
        });
      }
    
    private getLiveResultsTicker = (eventId: string) => {
      // console.log("Get live results ticker");
      var apiSampleModel = this.apiSampleViewModel.value;
      apiSampleModel.isLoading = true;
      this.apiSampleViewModel.next(apiSampleModel);
      this.tournamentService.GetLiveResultsTicker(eventId!).subscribe(apiResult => {
        var apiSampleModel = this.apiSampleViewModel.value;
        apiSampleModel.isLoading = false;
        apiSampleModel.response = apiResult?.result ?? apiResult.summaryTitle;
        this.apiSampleViewModel.next(apiSampleModel);
        });
      }
    
    private getStandingsTicker = (eventId: string) => {
      // console.log("Get standings ticker");
      var apiSampleModel = this.apiSampleViewModel.value;
      apiSampleModel.isLoading = true;
      this.apiSampleViewModel.next(apiSampleModel);
      this.tournamentService.GetStandingsTicker(eventId!).subscribe(apiResult => {
        var apiSampleModel = this.apiSampleViewModel.value;
        apiSampleModel.isLoading = false;
        apiSampleModel.response = (apiResult.isSuccess() ? apiResult.result : apiResult.summaryTitle) ?? "unknown error";
        this.apiSampleViewModel.next(apiSampleModel);
      });
    }

    private getEndOfRoundMessage = (eventId: string) => {
      // console.log("Get endOfRound message");
      var apiSampleModel = this.apiSampleViewModel.value;
      apiSampleModel.isLoading = true;
      this.apiSampleViewModel.next(apiSampleModel);
      this.tournamentService.GetEndOfRoundMessage(eventId!).subscribe(apiResult => {
        var apiSampleModel = this.apiSampleViewModel.value;
        apiSampleModel.isLoading = false;
        apiSampleModel.response = apiResult?.result ?? apiResult.summaryTitle;
        this.apiSampleViewModel.next(apiSampleModel);
      });
    }

    private updateResultsTab = (tab: apiSampleTabOptions) => {
      switch (tab) {
        case apiSampleTabOptions.liveResults:
          this.getLiveResultsTicker(this.eventId);
          break;
        case apiSampleTabOptions.pairings:
          this.getPairingsTicker(this.eventId);
          break;
        case apiSampleTabOptions.standings:
          this.getStandingsTicker(this.eventId);
          break;
        case apiSampleTabOptions.endOfRound:
          this.getEndOfRoundMessage(this.eventId);
          break;
      }
    }
}